import {
  createLibraryEntryFn,
  createLibraryPathFn,
  listLibraryAdminFn,
  lookupDoiFn,
  updateLibraryEntryFn,
  updateLibraryPathFn,
} from "@/actions/library/handler";
import { sharedContentTopics } from "@/api/db/schema/content";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { slugify } from "@/utils/slug";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  BookOpen,
  Database,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/library")({
  beforeLoad: ({ context }) => {
    if (
      !context.user ||
      (context.user.role !== "admin" && context.user.role !== "author")
    )
      throw redirect({ to: "/" });
  },
  component: AdminLibraryPage,
});

const emptyEntry = {
  doi: "",
  sourceUrl: "",
  openAccessUrl: "",
  originalTitle: "",
  turkishTitle: "",
  authors: "",
  journal: "",
  publicationDate: "",
  studyType: "original_research",
  difficulty: "beginner",
  summary: "",
  findings: "",
  limitations: "",
  practicalTakeaways: "",
  chronologyContext: "",
  coachCommentary: "",
  topics: [] as string[],
  rightsStatus: "metadata_only",
  licenseUrl: "",
  rightsConfirmed: false,
  ctaHref: "",
  ctaLabel: "",
  status: "draft",
  pathIds: [] as string[],
  questionIds: [] as string[],
};

function AdminLibraryPage() {
  const library = useQuery({
    queryKey: ["admin-library"],
    queryFn: () => listLibraryAdminFn(),
  });
  const [pathTitle, setPathTitle] = useState("");
  const [pathDescription, setPathDescription] = useState("");
  const [pathCoverImageUrl, setPathCoverImageUrl] = useState("");
  const [pathTopics, setPathTopics] = useState<string[]>([]);
  const [editingPathId, setEditingPathId] = useState<string | null>(null);
  const [entry, setEntry] = useState(emptyEntry);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

  const savePath = useMutation({
    mutationFn: () =>
      editingPathId
        ? updateLibraryPathFn({
            data: {
              pathId: editingPathId,
              title: pathTitle,
              slug: slugify(pathTitle),
              description: pathDescription,
              topics: pathTopics as never,
              isPublished:
                library.data?.paths.find(
                  (path) => path.pathId === editingPathId,
                )?.isPublished || false,
              coverImageUrl: pathCoverImageUrl || null,
            },
          })
        : createLibraryPathFn({
            data: {
              title: pathTitle,
              slug: slugify(pathTitle),
              description: pathDescription,
              topics: pathTopics as never,
              isPublished: false,
              coverImageUrl: pathCoverImageUrl || null,
            },
          }),
    onSuccess: () => {
      setPathTitle("");
      setPathDescription("");
      setPathCoverImageUrl("");
      setPathTopics([]);
      setEditingPathId(null);
      void library.refetch();
      toast.success("Öğrenme yolu kaydedildi.");
    },
    onError: (error) => toast.error(error.message),
  });
  const lookup = useMutation({
    mutationFn: () => lookupDoiFn({ data: { doi: entry.doi } }),
    onSuccess: ({ metadata }) =>
      setEntry((current) => ({
        ...current,
        doi: metadata.doi,
        sourceUrl: metadata.sourceUrl,
        originalTitle: metadata.originalTitle,
        turkishTitle: current.turkishTitle.trim() || metadata.originalTitle,
        authors: metadata.authors.join("; "),
        journal: metadata.journal || "",
        publicationDate: metadata.publicationDate,
      })),
    onError: (error) => toast.error(error.message),
  });
  const saveEntry = useMutation({
    mutationFn: () => {
      const turkishTitle =
        entry.turkishTitle.trim() || entry.originalTitle.trim();
      const slug = slugify(turkishTitle || entry.originalTitle);
      return (editingEntryId ? updateLibraryEntryFn : createLibraryEntryFn)({
        data: {
          ...(editingEntryId ? { entryId: editingEntryId } : {}),
          slug,
          doi: entry.doi || null,
          sourceUrl: entry.sourceUrl,
          openAccessUrl: entry.openAccessUrl || null,
          originalTitle: entry.originalTitle,
          turkishTitle,
          authors: entry.authors
            .split(";")
            .map((author) => author.trim())
            .filter(Boolean),
          journal: entry.journal || null,
          publicationDate: entry.publicationDate,
          studyType: entry.studyType as "original_research",
          difficulty: entry.difficulty as "beginner",
          summary: entry.summary,
          findings: entry.findings,
          limitations: entry.limitations,
          practicalTakeaways: entry.practicalTakeaways,
          chronologyContext: entry.chronologyContext,
          coachCommentary: entry.coachCommentary,
          topics: entry.topics as never,
          rightsStatus: entry.rightsStatus as "metadata_only",
          licenseUrl: entry.licenseUrl || null,
          rightsCheckedAt: entry.rightsConfirmed ? new Date() : null,
          ctaHref: entry.ctaHref || null,
          ctaLabel: entry.ctaLabel || null,
          editorialContent: null,
          status: entry.status as "draft",
          pathIds: entry.pathIds,
          questionIds: entry.questionIds,
        },
      } as never);
    },
    onSuccess: () => {
      setEntry(emptyEntry);
      setEditingEntryId(null);
      void library.refetch();
      toast.success("Bilimsel kaynak kaydedildi.");
    },
    onError: (error) => {
      try {
        const parsed = JSON.parse(error.message);
        if (Array.isArray(parsed)) {
          const messages = parsed.map(
            (e: { path?: string[]; message?: string }) => {
              const field = e.path?.[0];
              if (field === "turkishTitle" || field === "slug")
                return "Türkçe başlık en az 3 karakter olmalıdır.";
              if (field === "originalTitle")
                return "Orijinal başlık en az 3 karakter olmalıdır.";
              if (field === "summary")
                return "Özet en az 40 karakter olmalıdır.";
              if (field === "findings")
                return "Bulgular en az 40 karakter olmalıdır.";
              if (field === "limitations")
                return "Sınırlılıklar en az 20 karakter olmalıdır.";
              if (field === "practicalTakeaways")
                return "Pratik çıkarımlar en az 40 karakter olmalıdır.";
              if (field === "pathIds")
                return "Yayınlanan kaynak en az bir öğrenme yoluna bağlı olmalıdır.";
              if (field === "topics")
                return "Yayınlanan kaynak en az bir konu etiketi taşımalıdır.";
              if (field === "rightsCheckedAt")
                return "Yayın öncesinde kullanım hakları onaylanmalıdır.";
              return e.message || "Geçersiz form verisi";
            },
          );
          toast.error(messages.join(". "));
          return;
        }
      } catch {
        // Not JSON
      }
      toast.error(error.message);
    },
  });

  const togglePath = useMutation({
    mutationFn: (path: NonNullable<typeof library.data>["paths"][number]) =>
      updateLibraryPathFn({
        data: {
          pathId: path.pathId,
          title: path.title,
          slug: path.slug,
          description: path.description,
          topics: path.topics as never,
          coverImageUrl: path.coverImageUrl,
          isPublished: !path.isPublished,
        },
      }),
    onSuccess: () => void library.refetch(),
    onError: (error) => toast.error(error.message),
  });
  const toggleEntry = useMutation({
    mutationFn: (item: NonNullable<typeof library.data>["entries"][number]) =>
      updateLibraryEntryFn({
        data: {
          entryId: item.entryId,
          slug: item.slug,
          doi: item.doi,
          sourceUrl: item.sourceUrl,
          openAccessUrl: item.openAccessUrl,
          originalTitle: item.originalTitle,
          turkishTitle: item.turkishTitle,
          authors: item.authors,
          journal: item.journal,
          publicationDate: item.publicationDate,
          studyType: item.studyType,
          difficulty: item.difficulty,
          summary: item.summary,
          findings: item.findings,
          limitations: item.limitations,
          practicalTakeaways: item.practicalTakeaways,
          chronologyContext: item.chronologyContext,
          coachCommentary: item.coachCommentary,
          topics: item.topics as never,
          rightsStatus: item.rightsStatus,
          licenseUrl: item.licenseUrl,
          rightsCheckedAt: item.rightsCheckedAt,
          ctaHref: item.ctaHref,
          ctaLabel: item.ctaLabel,
          editorialContent: item.editorialContent,
          status: item.status === "published" ? "draft" : "published",
          pathIds: item.paths.map((pathRelation) => pathRelation.path.pathId),
          questionIds: item.questions.map((question) => question.questionId),
        },
      }),
    onSuccess: () => void library.refetch(),
    onError: (error) => toast.error(error.message),
  });

  return (
    <main className="container mx-auto max-w-7xl px-4 py-24">
      <header>
        <h1 className="font-bold text-3xl">Bilim Kütüphanesi Yönetimi</h1>
        <p className="mt-2 text-muted-foreground">
          Öğrenme yollarını ve açıklamalı bilimsel kaynakları hazırlayın.
        </p>
      </header>

      <div className="mt-8 grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Yeni öğrenme yolu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="Başlık">
                <Input
                  value={pathTitle}
                  onChange={(e) => setPathTitle(e.target.value)}
                />
              </Field>
              <Field label="Açıklama">
                <Textarea
                  value={pathDescription}
                  onChange={(e) => setPathDescription(e.target.value)}
                  rows={5}
                />
              </Field>
              <Field label="Kapak görseli URL (isteğe bağlı)">
                <Input
                  type="url"
                  value={pathCoverImageUrl}
                  onChange={(event) => setPathCoverImageUrl(event.target.value)}
                />
              </Field>
              <TopicPicker value={pathTopics} onChange={setPathTopics} />
              <Button
                onClick={() => savePath.mutate()}
                disabled={
                  savePath.isPending ||
                  pathTitle.length < 3 ||
                  pathDescription.length < 20
                }
              >
                {editingPathId ? (
                  <Pencil className="mr-2 h-4 w-4" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {editingPathId ? "Değişiklikleri kaydet" : "Yolu oluştur"}
              </Button>
              {editingPathId && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditingPathId(null);
                    setPathTitle("");
                    setPathDescription("");
                    setPathCoverImageUrl("");
                    setPathTopics([]);
                  }}
                >
                  <X className="mr-2 h-4 w-4" /> İptal
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Öğrenme yolları</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {library.data?.paths.map((path) => (
                <div key={path.pathId} className="rounded-lg border p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <strong>{path.title}</strong>
                      <p className="mt-1 text-muted-foreground text-xs">
                        {path.entries.length} kaynak
                      </p>
                    </div>
                    <Badge variant={path.isPublished ? "default" : "outline"}>
                      {path.isPublished ? "Yayında" : "Taslak"}
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    onClick={() => togglePath.mutate(path)}
                  >
                    {path.isPublished ? "Yayından kaldır" : "Yayınla"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="mt-3"
                    onClick={() => {
                      setEditingPathId(path.pathId);
                      setPathTitle(path.title);
                      setPathDescription(path.description);
                      setPathCoverImageUrl(path.coverImageUrl || "");
                      setPathTopics(path.topics);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    <Pencil className="mr-1 h-4 w-4" /> Düzenle
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {editingEntryId
                ? "Bilimsel kaynağı düzenle"
                : "Yeni bilimsel kaynak"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <Field label="DOI">
                <Input
                  value={entry.doi}
                  onChange={(e) => setEntry({ ...entry, doi: e.target.value })}
                  placeholder="10.xxxx/xxxxx"
                />
              </Field>
              <Button
                className="self-end"
                variant="outline"
                onClick={() => lookup.mutate()}
                disabled={lookup.isPending || entry.doi.length < 5}
              >
                {lookup.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Database className="mr-2 h-4 w-4" />
                )}
                Metadata getir
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Orijinal başlık">
                <Input
                  value={entry.originalTitle}
                  onChange={(e) =>
                    setEntry({ ...entry, originalTitle: e.target.value })
                  }
                />
              </Field>
              <Field label="Türkçe başlık *">
                <Input
                  placeholder="Makalenin Türkçe başlığı (en az 3 karakter)"
                  value={entry.turkishTitle}
                  onChange={(e) =>
                    setEntry({ ...entry, turkishTitle: e.target.value })
                  }
                />
              </Field>
              <Field label="Yazarlar (; ile ayırın)">
                <Input
                  value={entry.authors}
                  onChange={(e) =>
                    setEntry({ ...entry, authors: e.target.value })
                  }
                />
              </Field>
              <Field label="Dergi">
                <Input
                  value={entry.journal}
                  onChange={(e) =>
                    setEntry({ ...entry, journal: e.target.value })
                  }
                />
              </Field>
              <Field label="Yayın tarihi">
                <Input
                  type="date"
                  value={entry.publicationDate}
                  onChange={(e) =>
                    setEntry({ ...entry, publicationDate: e.target.value })
                  }
                />
              </Field>
              <Field label="Kaynak URL">
                <Input
                  type="url"
                  value={entry.sourceUrl}
                  onChange={(e) =>
                    setEntry({ ...entry, sourceUrl: e.target.value })
                  }
                />
              </Field>
              <Field label="Açık erişim URL (varsa)">
                <Input
                  type="url"
                  value={entry.openAccessUrl}
                  onChange={(e) =>
                    setEntry({ ...entry, openAccessUrl: e.target.value })
                  }
                />
              </Field>
              <Field label="Çalışma türü">
                <Select
                  value={entry.studyType}
                  onValueChange={(value) =>
                    setEntry({ ...entry, studyType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      "original_research",
                      "systematic_review",
                      "meta_analysis",
                      "position_stand",
                      "narrative_review",
                      "other",
                    ].map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replaceAll("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Seviye">
                <Select
                  value={entry.difficulty}
                  onValueChange={(value) =>
                    setEntry({ ...entry, difficulty: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Başlangıç</SelectItem>
                    <SelectItem value="intermediate">Orta</SelectItem>
                    <SelectItem value="advanced">İleri</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Kısa özet">
              <Textarea
                value={entry.summary}
                onChange={(e) =>
                  setEntry({ ...entry, summary: e.target.value })
                }
                rows={4}
              />
            </Field>
            <Field label="Bu çalışma ne söylüyor?">
              <Textarea
                value={entry.findings}
                onChange={(e) =>
                  setEntry({ ...entry, findings: e.target.value })
                }
                rows={4}
              />
            </Field>
            <Field label="Sınırlılıklar">
              <Textarea
                value={entry.limitations}
                onChange={(e) =>
                  setEntry({ ...entry, limitations: e.target.value })
                }
                rows={4}
              />
            </Field>
            <Field label="Sporcu ve koç için pratik karşılığı">
              <Textarea
                value={entry.practicalTakeaways}
                onChange={(e) =>
                  setEntry({ ...entry, practicalTakeaways: e.target.value })
                }
                rows={4}
              />
            </Field>
            <Field label="Kronolojide neden burada?">
              <Textarea
                value={entry.chronologyContext}
                onChange={(e) =>
                  setEntry({ ...entry, chronologyContext: e.target.value })
                }
                rows={3}
                placeholder="Bu çalışmanın önceki ve sonraki kaynaklar arasındaki yerini açıklayın."
              />
            </Field>
            <Field label="Koç yorumu">
              <Textarea
                value={entry.coachCommentary}
                onChange={(e) =>
                  setEntry({ ...entry, coachCommentary: e.target.value })
                }
                rows={4}
              />
            </Field>
            <TopicPicker
              value={entry.topics}
              onChange={(topics) => setEntry({ ...entry, topics })}
            />
            <div>
              <Label>Öğrenme yolları</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {library.data?.paths.map((path) => (
                  <label
                    key={path.pathId}
                    className="flex items-center gap-2 rounded-md border p-2 text-sm"
                  >
                    <Checkbox
                      checked={entry.pathIds.includes(path.pathId)}
                      onCheckedChange={(checked) =>
                        setEntry({
                          ...entry,
                          pathIds: checked
                            ? [...entry.pathIds, path.pathId]
                            : entry.pathIds.filter((id) => id !== path.pathId),
                        })
                      }
                    />
                    {path.title}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>İlgili forum soruları</Label>
              <div className="mt-2 grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2">
                {library.data?.questions.map((question) => (
                  <label
                    key={question.tagId}
                    className="flex items-start gap-2 rounded-md border p-2 text-sm"
                  >
                    <Checkbox
                      checked={entry.questionIds.includes(question.tagId)}
                      onCheckedChange={(checked) =>
                        setEntry({
                          ...entry,
                          questionIds: checked
                            ? [...entry.questionIds, question.tagId]
                            : entry.questionIds.filter(
                                (id) => id !== question.tagId,
                              ),
                        })
                      }
                    />
                    {question.tagTitle}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-4 rounded-xl border bg-muted/20 p-4 md:grid-cols-2">
              <Field label="Hak durumu">
                <Select
                  value={entry.rightsStatus}
                  onValueChange={(value) =>
                    setEntry({ ...entry, rightsStatus: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="metadata_only">
                      Metadata + özgün yorum
                    </SelectItem>
                    <SelectItem value="open_access">Açık erişim</SelectItem>
                    <SelectItem value="licensed_reuse">
                      Yeniden kullanım lisansı
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Lisans URL (varsa)">
                <Input
                  type="url"
                  value={entry.licenseUrl}
                  onChange={(e) =>
                    setEntry({ ...entry, licenseUrl: e.target.value })
                  }
                />
              </Field>
              <label className="flex items-start gap-3 md:col-span-2">
                <Checkbox
                  checked={entry.rightsConfirmed}
                  onCheckedChange={(checked) =>
                    setEntry({ ...entry, rightsConfirmed: Boolean(checked) })
                  }
                />
                <span className="text-sm">
                  Kaynak, lisans ve kullanılan materyallerin yayın hakkını
                  kontrol ettim.
                </span>
              </label>
            </div>
            <div className="grid gap-4 rounded-xl border p-4 md:grid-cols-2">
              <Field label="Bağlamsal CTA adresi">
                <Input
                  value={entry.ctaHref}
                  onChange={(e) =>
                    setEntry({ ...entry, ctaHref: e.target.value })
                  }
                  placeholder="/essay/1rm-calculator"
                />
              </Field>
              <Field label="CTA buton metni">
                <Input
                  value={entry.ctaLabel}
                  onChange={(e) =>
                    setEntry({ ...entry, ctaLabel: e.target.value })
                  }
                  placeholder="1RM değerini hesapla"
                />
              </Field>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                checked={entry.status === "published"}
                onCheckedChange={(checked) =>
                  setEntry({
                    ...entry,
                    status: checked ? "published" : "draft",
                  })
                }
              />
              <Label>Onay sonrası yayınla</Label>
            </div>
            <Button
              onClick={() => saveEntry.mutate()}
              disabled={saveEntry.isPending || entry.topics.length === 0}
            >
              <BookOpen className="mr-2 h-4 w-4" />
              {editingEntryId ? "Değişiklikleri kaydet" : "Kaynağı kaydet"}
            </Button>
            {editingEntryId && (
              <Button
                variant="ghost"
                onClick={() => {
                  setEditingEntryId(null);
                  setEntry(emptyEntry);
                }}
              >
                <X className="mr-2 h-4 w-4" /> Düzenlemeyi iptal et
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Kaynaklar</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {library.data?.entries.map((item) => (
            <div key={item.entryId} className="rounded-lg border p-4">
              <div className="flex justify-between gap-3">
                <strong>{item.turkishTitle}</strong>
                <Badge
                  variant={item.status === "published" ? "default" : "outline"}
                >
                  {item.status}
                </Badge>
              </div>
              <p className="mt-2 text-muted-foreground text-xs">
                {item.publicationDate.slice(0, 4)} · {item.journal}
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleEntry.mutate(item)}
                >
                  {item.status === "published" ? "Taslağa al" : "Yayınla"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingEntryId(item.entryId);
                    setEntry({
                      doi: item.doi || "",
                      sourceUrl: item.sourceUrl,
                      openAccessUrl: item.openAccessUrl || "",
                      originalTitle: item.originalTitle,
                      turkishTitle: item.turkishTitle,
                      authors: item.authors.join("; "),
                      journal: item.journal || "",
                      publicationDate: item.publicationDate,
                      studyType: item.studyType,
                      difficulty: item.difficulty,
                      summary: item.summary,
                      findings: item.findings,
                      limitations: item.limitations,
                      practicalTakeaways: item.practicalTakeaways,
                      chronologyContext: item.chronologyContext,
                      coachCommentary: item.coachCommentary,
                      topics: item.topics,
                      rightsStatus: item.rightsStatus,
                      licenseUrl: item.licenseUrl || "",
                      rightsConfirmed: Boolean(item.rightsCheckedAt),
                      ctaHref: item.ctaHref || "",
                      ctaLabel: item.ctaLabel || "",
                      status: item.status,
                      pathIds: item.paths.map(
                        (relation) => relation.path.pathId,
                      ),
                      questionIds: item.questions.map(
                        (question) => question.questionId,
                      ),
                    });
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <Pencil className="mr-1 h-4 w-4" /> Düzenle
                </Button>
                {item.status === "published" && (
                  <Button size="sm" variant="ghost" asChild>
                    <a
                      href={`/kutuphane/kaynak/${item.slug}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink className="mr-1 h-4 w-4" />
                      Görüntüle
                    </a>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </main>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function TopicPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (topics: string[]) => void;
}) {
  return (
    <div>
      <Label>Ortak konu etiketleri</Label>
      <div className="mt-2 flex flex-wrap gap-2">
        {sharedContentTopics.map((topic) => {
          const selected = value.includes(topic);
          return (
            <Button
              key={topic}
              type="button"
              size="sm"
              variant={selected ? "default" : "outline"}
              onClick={() =>
                onChange(
                  selected
                    ? value.filter((item) => item !== topic)
                    : [...value, topic],
                )
              }
            >
              {topic}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
